import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import Dialog from './ui/Dialog';
import Field, { FormServerError } from './ui/Field';
import CustomerPicker from './ui/CustomerPicker';
import EntityPicker from './ui/EntityPicker';
import PendingDecision from './ui/PendingDecision';
import Badge from './ui/Badge';
import { FormFooter } from './projects/formBits';
import { formatDateIntl, formatTime } from '../utils/format';
import {
  MEETING_TYPES, meetingLeadOptions, buildCreatePayload, buildUpdatePayload, conflictAcknowledged, conflictKey, dateOfDayKey,
  dayKeyOfDatetimeLocal, editNotifiesParticipants, emptyMeetingForm, formFromMeeting, pickCustomer, pickLead,
  reconcileStaff, validateMeetingForm, type LocationType, type MeetingDetailLike, type MeetingFormState, type MeetingType,
} from '../utils/meetingsModel';
import DateTimeInput from './ui/DateTimeInput';

// P-38b (D-192): the meeting form, for a new meeting and for editing one.
// Backwards compatible with Home "סדר היום" (`initialDateTime` / `initialDuration`).
// "מי המשתתפים?" replaces the old customer + lead fields (a customer OR a lead,
// then the people of that customer, then OTE staff). An employee marked
// "internal only" is not offered next to a customer or a lead (D-196). The date
// is checked against the meetings of the chosen staff on the same day (D-192 §6).

const CONFLICT_DEBOUNCE_MS = 400;

export interface EditableMeeting extends MeetingDetailLike {
  id: string;
  /** The lead's company name (GET /meetings/:id) — offered even when the lead was converted / lost. */
  lead_name?: string | null;
}

export default function CreateMeetingModal({
  onClose,
  onCreated,
  initialDateTime,
  initialDuration,
  meeting,
  presetOrganizationId,
  presetLeadId,
  presetMeetingType,
  initialTitle,
  gcalExternalEventId,
  initialMeetingUrl,
}: {
  onClose: () => void;
  /** Called after the meeting was created or saved (before the form closes). */
  onCreated?: () => void;
  /** `YYYY-MM-DDTHH:mm` in local time. */
  initialDateTime?: string;
  /** Minutes (the form's default is 30). */
  initialDuration?: number;
  /** Edit this meeting (GET /meetings/:id) instead of creating one. */
  meeting?: EditableMeeting;
  /** From a customer / lead page: the meeting is for them and they cannot be changed. */
  presetOrganizationId?: string;
  presetLeadId?: string;
  /** P-51 (D-224 s.4): the "השלב הבא" box opens the form with the meeting type filled (still changeable). */
  presetMeetingType?: MeetingFormState['meetingType'];
  /** FIX-D327: "להפוך לפגישה במערכת" from an event of the employee's Google calendar - its name and that event. */
  initialTitle?: string;
  gcalExternalEventId?: string;
  /** FIX-D344 (D-344 §10): the Google event's Zoom / meeting link ("קישור אחר"; its recording then comes in by itself). */
  initialMeetingUrl?: string;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const editing = !!meeting;
  const locked = !editing && !!(presetOrganizationId || presetLeadId);

  const [form, setForm] = useState<MeetingFormState>(() => {
    if (meeting) return formFromMeeting(meeting);
    return emptyMeetingForm({
      ...(initialTitle ? { title: initialTitle } : {}),
      dateTime: initialDateTime ?? '',
      duration: initialDuration && initialDuration >= 5 ? initialDuration : 30,
      organizationId: presetOrganizationId ?? '',
      leadId: presetLeadId ?? '',
      includeLead: !!presetLeadId,
      ...(presetMeetingType ? { meetingType: presetMeetingType } : {}),
      ...(initialMeetingUrl ? { meetingUrl: initialMeetingUrl, locationType: 'link' as const } : {}),
    });
  });
  const set = (patch: Partial<MeetingFormState>) => setForm((f) => ({ ...f, ...patch }));
  const [showErrors, setShowErrors] = useState(false);
  const organizerId = meeting?.organizer_id ?? meId;

  // ── FIX-D344 (D-344 §5): "איפה?" - Zoom only when it is on and the organizer has a Zoom license; then it is the default. ──
  const { data: zoomRes } = useQuery({
    queryKey: ['zoom', 'status', editing ? organizerId : 'me'],
    queryFn: () => api.getZoomStatus(editing && organizerId ? organizerId : undefined),
    staleTime: 5 * 60 * 1000,
  });
  const zoomOk = !!(zoomRes?.success && zoomRes.data?.enabled && zoomRes.data?.licensed);
  const zoomDefaulted = useRef(editing || !!initialMeetingUrl);
  useEffect(() => {
    if (zoomDefaulted.current || !zoomRes) return;
    zoomDefaulted.current = true;
    if (zoomOk) setForm((f) => (f.locationType ? f : { ...f, locationType: 'zoom' }));
  }, [zoomRes, zoomOk]);
  const placeOptions: LocationType[] = [...(zoomOk || form.locationType === 'zoom' ? ['zoom' as const] : []), 'office', 'phone', 'link'];
  const place: LocationType = form.locationType || 'link';
  const wasZoom = meeting?.location_type === 'zoom';

  // ── Who can be invited ──
  const ctxKey = `${form.organizationId}|${form.leadId}`;
  const { data: candRes, isFetching: candsFetching } = useQuery({
    queryKey: ['meetings', 'candidates', form.organizationId, form.leadId],
    queryFn: () => api.getMeetingCandidates({ organization_id: form.organizationId || undefined, lead_id: form.leadId || undefined }),
  });
  const cands = candRes?.success ? candRes.data : undefined;

  // The creator is ticked by default (and may be unticked); done once, when the list is known.
  const seeded = useRef(editing);
  // The context the staff list was last reconciled for (edit: the saved one is trusted as is).
  const reconciledFor = useRef<string | null>(editing ? ctxKey : null);
  useEffect(() => {
    if (!cands) return;
    if (!seeded.current) {
      seeded.current = true;
      if (meId && cands.staff.some((s) => s.user_id === meId)) {
        setForm((f) => (f.userIds.includes(meId) ? f : { ...f, userIds: [...f.userIds, meId] }));
      }
    }
    if (reconciledFor.current !== ctxKey) {
      reconciledFor.current = ctxKey;
      // D-196: whoever the new list does not offer (internal only) is dropped.
      setForm((f) => {
        const next = reconcileStaff(f.userIds, cands.staff);
        return next.length === f.userIds.length ? f : { ...f, userIds: next };
      });
    }
  }, [cands, ctxKey, meId]);

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  // ── Same-day conflicts (debounced) ──
  const liveKey = conflictKey(form, meeting?.id, organizerId);
  const [debouncedKey, setDebouncedKey] = useState(liveKey);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedKey(liveKey), CONFLICT_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [liveKey]);
  const { data: conflictRes } = useQuery({
    queryKey: ['meetings', 'conflicts', debouncedKey],
    queryFn: () => {
      const [date, ids, excl] = debouncedKey.split('|');
      return api.getMeetingConflicts({ date, user_ids: ids.split(','), exclude_id: excl || undefined });
    },
    enabled: !!debouncedKey,
  });
  const conflicts = debouncedKey && debouncedKey === liveKey && conflictRes?.success ? conflictRes.data : undefined;
  const hasConflict = !!conflicts?.has_conflict;
  const [ackKey, setAckKey] = useState('');
  const acknowledged = conflictAcknowledged(ackKey, liveKey);

  // ── Leads (for the lead picker) ──
  const { data: leadsRes } = useQuery({
    queryKey: ['leads', 'active'],
    queryFn: () => api.getLeads({ limit: 500 }),
    enabled: !locked,
  });
  // FIX-B17 (QA-P38b קל 2) — the meeting's own lead stays offered after it was converted / lost.
  const leads = meetingLeadOptions<any>(
    leadsRes?.data ?? [],
    form.leadId,
    meeting?.lead_id ? { id: meeting.lead_id, company_name: meeting.lead_name ?? cands?.lead?.name ?? '', contact_name: cands?.lead?.contact_name ?? null } : null,
  );

  const errors = validateMeetingForm(form);
  const invalid = Object.keys(errors).length > 0;
  const notice = meeting ? editNotifiesParticipants(meeting, form) : { time: false, link: false };

  const saveMutation = useMutation({
    mutationFn: () => (meeting
      ? api.updateMeeting(meeting.id, buildUpdatePayload(form, organizerId))
      : api.createMeeting(gcalExternalEventId
        ? { ...buildCreatePayload(form, meId), gcal_external_event_id: gcalExternalEventId }
        : buildCreatePayload(form, meId))),
    onSuccess: (res) => {
      if (res.success) {
        queryClient.invalidateQueries({ queryKey: ['meetings'] });
        queryClient.invalidateQueries({ queryKey: ['meeting'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['lead'] });
        queryClient.invalidateQueries({ queryKey: ['lead-journey'] }); // DQ-382
        queryClient.invalidateQueries({ queryKey: ['lead-timeline'] });
        onCreated?.();
        onClose();
      }
    },
  });

  const blockedByConflict = hasConflict && !acknowledged;
  // FIX-B17 (QA-P38b קל 3) — after switching customer <-> lead the people of the
  // new context are still loading: no submit until they are known (the staff
  // list is reconciled against them, D-196).
  const candidatesLoading = !cands || candsFetching;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (invalid || blockedByConflict || candidatesLoading) return;
    saveMutation.mutate();
  };

  const dayLabel = (key: string) => formatDateIntl(dateOfDayKey(key), { weekday: 'long', day: 'numeric', month: 'long' }, key, i18n.language);
  const applyFreeDay = (key: string) => {
    const time = form.dateTime.includes('T') ? form.dateTime.split('T')[1] : '09:00';
    set({ dateTime: `${key}T${time}` });
  };

  const typeOptions = useMemo(() => MEETING_TYPES.map((k) => ({ key: k, label: t(`meetings.type.${k}`) })), [t]);
  const sectionTitle = 'text-sm font-semibold text-surface-900';

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? t('meetings.editModalTitle') : t('meetings.createModalTitle')}
      size="lg"
      dismissible={!saveMutation.isPending}
      footer={(
        <FormFooter
          formId="meeting-form"
          busy={saveMutation.isPending}
          submitLabel={editing ? t('meetings.submitSave') : t('meetings.submitCreate')}
          onCancel={onClose}
          disabled={blockedByConflict || candidatesLoading}
        />
      )}
    >
      <form id="meeting-form" noValidate onSubmit={submit} className="space-y-4">
        <FormServerError error={saveMutation.error} fallback={editing ? t('meetings.saveError') : t('meetings.createError')} />

        <Field label={t('meetings.fieldTitle')} required error={showErrors && errors.title ? t('meetings.errorTitle') : undefined}>
          <input type="text" value={form.title} onChange={(e) => set({ title: e.target.value })} className="input" />
        </Field>

        <Field label={t('meetings.fieldType')} required error={showErrors && errors.meetingType ? t('meetings.errorType') : undefined}>
          <select
            value={form.meetingType}
            onChange={(e) => set({ meetingType: e.target.value as MeetingType | '' })}
            className="input"
          >
            <option value="">{t('meetings.typePlaceholder')}</option>
            {typeOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Field>
        {form.meetingType === 'other' && (
          <Field label={t('meetings.fieldTypeOther')} optional>
            <input type="text" value={form.meetingTypeOther} maxLength={255} onChange={(e) => set({ meetingTypeOther: e.target.value })} className="input" />
          </Field>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('meetings.fieldDateTime')} required error={showErrors && errors.dateTime ? t('meetings.errorDateTime') : undefined}>
            <DateTimeInput dir="ltr" value={form.dateTime} onChange={(e) => set({ dateTime: e.target.value })} className="input" />
          </Field>
          <Field label={t('meetings.fieldDuration')} error={showErrors && errors.duration ? t('meetings.errorDuration') : undefined}>
            <input
              type="number"
              min={5}
              step={5}
              value={form.duration}
              onChange={(e) => set({ duration: Number(e.target.value) || 30 })}
              className="input"
            />
          </Field>
        </div>

        {hasConflict && conflicts && (
          <div role="status" className="rounded-lg border border-warning-200 bg-warning-50 px-3 py-3 text-sm text-warning-800 space-y-2">
            <p className="font-medium">
              {t('meetings.conflictTitle', { day: dayLabel(dayKeyOfDatetimeLocal(form.dateTime) || conflicts.date) })}
            </p>
            <ul className="space-y-1">
              {conflicts.items.map((c, i) => (
                <li key={`${c.meeting_id ?? 'p'}-${i}`}>
                  <bdi>{c.title}</bdi>
                  <span className="text-warning-800"> · <span dir="ltr">{formatTime(c.start)}</span>–<span dir="ltr">{formatTime(c.end)}</span>
                    {c.user_names.length > 0 && <> · <bdi>{c.user_names.join(', ')}</bdi></>}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-2">
              {conflicts.next_free_date && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => applyFreeDay(conflicts.next_free_date as string)}>
                  {t('meetings.conflictNextFree', { day: dayLabel(conflicts.next_free_date) })}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                aria-pressed={acknowledged}
                onClick={() => setAckKey(acknowledged ? '' : liveKey)}
              >
                {acknowledged ? t('meetings.conflictContinuing') : t('meetings.conflictContinue')}
              </button>
            </div>
          </div>
        )}

        <Field label={t('meetings.fieldDescription')} optional>
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} className="input resize-y" />
        </Field>

        <Field label={t('zoom.form.where')}>
          <select value={place} onChange={(e) => set({ locationType: e.target.value as LocationType })} className="input">
            {placeOptions.map((o) => <option key={o} value={o}>{t(`zoom.form.place.${o}`)}</option>)}
          </select>
        </Field>
        {place === 'zoom' && (
          <p role="status" className="rounded-lg bg-info-50 px-3 py-2 text-sm text-info-800">
            {wasZoom && form.meetingUrl ? t('zoom.form.zoomExisting') : t('zoom.form.zoomNew')}
          </p>
        )}
        {(place === 'office' || place === 'phone') && (
          <Field label={place === 'office' ? t('zoom.form.officeAddress') : t('zoom.form.phoneNumber')} optional>
            <input
              type="text"
              dir={place === 'phone' ? 'ltr' : undefined}
              value={form.locationText}
              maxLength={500}
              onChange={(e) => set({ locationText: e.target.value })}
              className="input"
            />
          </Field>
        )}
        {place === 'link' && (
          <Field
            label={t('meetings.fieldMeetingUrl')}
            optional
            hint={<span className="inline-flex items-center gap-1">{t('meetings.zoomHint')} <PendingDecision id="PM-23" /></span>}
          >
            <input
              type="url"
              dir="ltr"
              value={form.meetingUrl}
              onChange={(e) => set({ meetingUrl: e.target.value })}
              className="input"
              placeholder={t('meetings.fieldMeetingUrlPlaceholder')}
            />
          </Field>
        )}

        <fieldset className="space-y-3 border-t border-surface-200 pt-4">
          <legend className={`${sectionTitle} mb-1`}>{t('meetings.whoTitle')}</legend>

          {locked ? (
            <p className="text-sm text-surface-700">
              {presetOrganizationId ? t('meetings.forThisCustomer') : t('meetings.forThisLead')}
            </p>
          ) : (
            <>
              <p className="text-xs text-surface-600">{t('meetings.whoHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CustomerPicker
                  value={form.organizationId}
                  onChange={(id) => setForm((f) => pickCustomer(f, id))}
                  label={t('meetings.fieldOrganization')}
                  placeholder={t('meetings.orgNone')}
                />
                <div>
                  <label htmlFor="mt-lead" className="label">{t('meetings.fieldLead')}</label>
                  <EntityPicker
                    id="mt-lead"
                    value={form.leadId}
                    onChange={(id) => setForm((f) => pickLead(f, id))}
                    emptyLabel={t('meetings.leadNone')}
                    options={leads.map((lead: any) => ({
                      value: lead.id,
                      label: lead.company_name,
                      secondary: lead.contact_name || lead.email || undefined,
                      keywords: [lead.contact_name, lead.email, lead.phone].filter(Boolean).join(' ') || undefined,
                    }))}
                  />
                </div>
              </div>
            </>
          )}

          {form.organizationId && cands && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-surface-700">{t('meetings.customerPeople')}</p>
              {cands.primary_email && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.includePrimaryEmail} onChange={(e) => set({ includePrimaryEmail: e.target.checked })} />
                  <span>{t('meetings.primaryEmail')} <bdi dir="ltr" className="text-surface-600">{cands.primary_email.email}</bdi></span>
                </label>
              )}
              {cands.contacts.map((c) => (
                <label key={c.id} className="flex flex-wrap items-center gap-x-2 text-sm">
                  <input type="checkbox" checked={form.contactIds.includes(c.id)} onChange={() => set({ contactIds: toggle(form.contactIds, c.id) })} />
                  <span><bdi>{c.name}</bdi></span>
                  {c.email && <bdi dir="ltr" className="text-xs text-surface-600">{c.email}</bdi>}
                  {c.has_portal && <Badge tone="info">{t('meetings.hasPortal')}</Badge>}
                </label>
              ))}
              {!cands.primary_email && cands.contacts.length === 0 && (
                <p className="text-xs text-surface-600">{t('meetings.noCustomerPeople')}</p>
              )}
            </div>
          )}

          {form.leadId && cands?.lead && (
            <label className="flex flex-wrap items-center gap-x-2 text-sm">
              <input type="checkbox" checked={form.includeLead} onChange={(e) => set({ includeLead: e.target.checked })} />
              <span>{t('meetings.leadAttends')} <bdi>{cands.lead.contact_name || cands.lead.name}</bdi></span>
              {cands.lead.email && <bdi dir="ltr" className="text-xs text-surface-600">{cands.lead.email}</bdi>}
            </label>
          )}

          <div className="space-y-1.5">
            <p className="text-xs font-medium text-surface-700">{t('meetings.oteTeam')}</p>
            {!cands && <p className="text-xs text-surface-600">{t('meetings.loading')}</p>}
            {cands?.staff.map((s) => {
              const isOrganizer = s.user_id === organizerId;
              // On an edit the organizer always stays (the server keeps them).
              const fixed = editing && isOrganizer;
              return (
                <label key={s.user_id} className="flex flex-wrap items-center gap-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={fixed || form.userIds.includes(s.user_id)}
                    disabled={fixed}
                    onChange={() => set({ userIds: toggle(form.userIds, s.user_id) })}
                  />
                  <span><bdi>{s.name}</bdi></span>
                  {isOrganizer && <Badge tone="neutral">{t('meetings.organizerTag')}</Badge>}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.aiAssist} onChange={(e) => set({ aiAssist: e.target.checked })} />
            <span>{t('meetings.aiAssist')}</span>
          </label>
          <PendingDecision id="PM-22" />
        </div>

        {editing && (notice.time || notice.link) && (
          <p role="status" className="rounded-lg bg-info-50 px-3 py-2 text-sm text-info-800">
            {notice.time ? t('meetings.editNoticeTime') : t('meetings.editNoticeLink')}
          </p>
        )}
        {blockedByConflict && (
          <p className="text-xs text-surface-600">{t('meetings.conflictBlockedHint')}</p>
        )}
      </form>
    </Dialog>
  );
}
